// JOBE-0167.6: RecentForgetFeed — "Recent forget events · across fleet" feed on W6.
//
// Mirrors RecentCorrectionsFeed visual vocabulary (D18 — corrections and forgets
// are siblings in the governance event taxonomy). Sourced from
// GET /api/memory/forget-log?limit=10 (real path) or the
// memory.mock.forgetting fixture (flagged path). Shares the fixture flag with
// JOBE-0167.5's W8 Forget log tab — consistent demo state.
//
// Per spec, cascade counts are OMITTED from the W6 trimmed surface (full
// counts live on W8's Forget log tab). "View all →" links to
// /promptops/memory?tab=forget-log with drill context.

import { useEffect, useState, useCallback } from 'react';
import { useFlagStore } from '../lib/useFlagStore.js';
import { flaggedFetch } from '../lib/flags.js';
import { withDrillContext } from '../lib/drillContext.js';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import '../mock-wcc.js'; // registers fixtures with FIXTURES map
import './RecentForgetFeed.css';

const VIEW_ALL_URL = withDrillContext(
  '/promptops/memory?tab=forget-log',
  { from: 'w6', surface: 'recent-forget-feed' }
);

const LIFECYCLE_TONE = {
  tombstoned: 'tombstoned',
  hard_deleted: 'tombstoned',
};

function relativeTime(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60)     return `${s}s ago`;
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function titleCase(s) {
  if (!s) return '';
  return String(s).replace(/(\S+)/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function CopyableUrn({ urn }) {
  const [copied, setCopied] = useState(false);
  function handle(e) {
    e.stopPropagation();
    if (!urn) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(urn).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <span
      className={`recent-forget-feed__urn${copied ? ' recent-forget-feed__urn--copied' : ''}`}
      role="button"
      tabIndex={0}
      data-testid="forget-feed-urn"
      data-urn={urn || ''}
      onClick={handle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handle(e); } }}
    >
      <span>{urn || ''}</span>
      <span aria-hidden="true" className="recent-forget-feed__urn-glyph">{copied ? '✓' : '⎘'}</span>
    </span>
  );
}

function ForgetFeedRow({ event, isFirst }) {
  const ts = isFirst ? relativeTime(event.ts) : event.ts;
  const tone = LIFECYCLE_TONE[event.lifecycle] || 'tombstoned';
  const lifecycleLabel = event.lifecycle === 'hard_deleted' ? 'hard-deleted' : (event.lifecycle || 'tombstoned');

  return (
    <li className="recent-forget-feed__row" data-testid="forget-feed-row">
      <div className="recent-forget-feed__row-header">
        <span className="recent-forget-feed__ts">{ts}</span>
        <span className="recent-forget-feed__sep"> · </span>
        <span className="recent-forget-feed__operator">{titleCase(event.operator || '')}</span>
        <span className="recent-forget-feed__sep"> · </span>
        <span className="recent-forget-feed__reason-class">{event.reason_class || ''}</span>
      </div>
      <div className="recent-forget-feed__urn-row">
        <CopyableUrn urn={event.urn} />
        <StatusPill tone={tone} label={lifecycleLabel} />
      </div>
      {event.body_snippet && (
        <div className="recent-forget-feed__snippet">"{event.body_snippet}"</div>
      )}
    </li>
  );
}

/**
 * @param {object} props
 * @param {number} [props.limit=10]
 */
export function RecentForgetFeed({ limit = 10 }) {
  const overrides = useFlagStore((s) => s.overrides);
  const [state, setState] = useState('loading');
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    // Fixture path.
    const fx = flaggedFetch('memory.mock.forgetting', overrides);
    if (fx !== undefined) {
      const list = Array.isArray(fx) ? fx : (fx.events || []);
      setEvents(list.slice(0, limit));
      setState('loaded');
      return;
    }
    // Real path.
    try {
      const resp = await fetch(`/api/memory/forget-log?limit=${limit}`);
      if (!resp.ok) throw new Error(`forget-log ${resp.status}`);
      const json = await resp.json();
      setEvents((json.events || []).slice(0, limit));
      setState('loaded');
    } catch (err) {
      setError(err);
      setState('error');
    }
  }, [overrides, limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <section
      className="recent-forget-feed"
      aria-label="Recent forget events"
      data-testid="recent-forget-feed"
    >
      <header className="recent-forget-feed__header">
        <h2 className="recent-forget-feed__title">
          Recent forget events · across fleet
        </h2>
        <a
          className="recent-forget-feed__view-all"
          href={VIEW_ALL_URL}
          data-testid="forget-feed-view-all"
        >
          View all →
        </a>
      </header>
      <hr className="recent-forget-feed__rule" />
      {state === 'loading' && (
        <div className="recent-forget-feed__loading" data-testid="forget-feed-loading">
          Loading…
        </div>
      )}
      {state === 'error' && (
        <div className="recent-forget-feed__error" data-testid="forget-feed-error">
          Could not load forget events ·{' '}
          <button
            type="button"
            className="recent-forget-feed__retry"
            data-testid="forget-feed-retry"
            onClick={load}
          >
            Retry
          </button>
        </div>
      )}
      {state === 'loaded' && events.length === 0 && (
        <p className="recent-forget-feed__empty" data-testid="forget-feed-empty">
          No forget events in the last 30 days.
        </p>
      )}
      {state === 'loaded' && events.length > 0 && (
        <ul className="recent-forget-feed__list" data-testid="forget-feed-list">
          {events.map((e, i) => (
            <ForgetFeedRow
              key={e.id || `${e.urn}-${e.ts}-${i}`}
              event={e}
              isFirst={i === 0}
            />
          ))}
        </ul>
      )}
      <footer className="recent-forget-feed__footer">
        <a
          className="recent-forget-feed__cta"
          href={VIEW_ALL_URL}
        >
          Last 30 days · all
        </a>
      </footer>
    </section>
  );
}

export default RecentForgetFeed;
